import {
  Component,
  ViewContainerRef,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { GlobalStore } from '@maximizer/core/shared/data-access';
import { EntrySearchService } from '@maximizer/outlook/main/data-access';
import {
  CalendarStore,
  EntryLinkService,
  OutlookService,
  TeamMemberService,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  AttendeeAddEntryEvent,
  AttendeeItemMode,
  CalendarAttendee,
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
} from '@maximizer/outlook/shared/domain';
import {
  MenuItem,
  OUTLOOK_SHARED_UI,
  TranslateVerticalPipe,
  AttendeeNotifyEvent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  DropDownButtonComponent,
  KENDO_BUTTONS,
  KENDO_DROPDOWNBUTTON,
} from '@progress/kendo-angular-buttons';
import { KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';

@Component({
  selector: 'maximizer-outlook-calendar-attendee-item',
  templateUrl: './attendee-item.component.html',
  imports: [
    KENDO_BUTTONS,
    KENDO_DROPDOWNBUTTON,
    KENDO_TOOLTIPS,
    OUTLOOK_SHARED_UI,
    TranslatePipe,
    TranslateVerticalPipe,
    UI_PIPES,
  ],
})
export class OutlookCalendarAttendeeItemComponent {
  addEntryButton = viewChild<DropDownButtonComponent>('addEntryButton');
  actionButton = viewChild<DropDownButtonComponent>('actionButton');

  attendee = input.required<CalendarAttendee>();
  userEmails = input<Set<string>>(new Set());
  mode = input<AttendeeItemMode>('read');
  popupContainer = input<ViewContainerRef | undefined>(undefined);

  addEntry = output<AttendeeAddEntryEvent>();
  navigate = output<CalendarAttendee>();
  notify = output<AttendeeNotifyEvent>();

  isLead = computed(() => this.attendee().type === EntryType.Lead);
  isCompany = computed(() => this.attendee().abentryType === EntryType.Company);

  addEntryMenuItems = computed<MenuItem[]>(() => {
    const rights = this.globalStore.session()?.user?.rights;
    return [
      {
        id: EntryType.Individual,
        text: 'outlook.main.actions.addIndividual',
        route: '/abentry/add/individual',
        icon: 'fa-icons icon-regular icon-18 icon-building-user',
        disabled: !rights?.addressBook?.insert,
        title: 'outlook.entries.individuals',
      },
      {
        id: EntryType.Contact,
        text: 'outlook.main.actions.addContact',
        route: '/abentry/add/contact',
        icon: 'fa-icons icon-solid icon-18 icon-user',
        disabled: !rights?.addressBook?.insert,
        title: 'outlook.entries.contacts',
      },
      {
        id: EntryType.Lead,
        text: 'outlook.main.actions.addLead',
        route: '/lead/add',
        icon: 'fa-icons icon-solid icon-18 icon-user-magnifying-glass',
        disabled: !rights?.lead?.insert,
        title: 'outlook.entries.leads',
      },
    ];
  });

  actionItems = computed<MenuItem[]>(() =>
    this.mode() === 'compose'
      ? this.composeActionItems()
      : this.readActionItems(),
  );

  private readonly composeActionItems = computed<MenuItem[]>(() => {
    const attendee = this.attendee();
    return [
      {
        id: attendee.isRequired ? 'move-optional' : 'move-required',
        text: attendee.isRequired
          ? this.translate.instant('outlook.calendar.compose.moveToOptional')
          : this.translate.instant('outlook.calendar.compose.moveToRequired'),
        click: () => void this.moveAttendee(attendee),
      },
      {
        id: 'remove',
        text: this.translate.instant('outlook.buttons.remove'),
        click: () => void this.removeAttendee(attendee),
      },
    ];
  });

  private readonly readActionItems = computed<MenuItem[]>(() => {
    const attendee = this.attendee();
    return [
      {
        id: 'view-details',
        text: this.translate.instant('outlook.entryPage.actions.viewDetails'),
        click: () => this.onNavigate(),
      },
      {
        id: 'copy-entry',
        text: this.translate.instant('outlook.entryPage.actions.copyEntry'),
        disabled: !attendee.id || !attendee.type,
        click: () => void this.copyEntryLink(attendee),
      },
    ];
  });

  readonly EntryType = EntryType;

  constructor(
    public readonly viewContainerRef: ViewContainerRef,
    private readonly translate: TranslateService,
    private readonly calendarStore: CalendarStore,
    private readonly outlookService: OutlookService,
    private readonly teamMemberService: TeamMemberService,
    private readonly tracker: TrackerService,
    private readonly globalStore: GlobalStore,
    private readonly entryLinkService: EntryLinkService,
    private readonly entrySearchService: EntrySearchService,
  ) {}

  isTeamMember(email: string): boolean {
    return this.teamMemberService.isTeamMember(email);
  }

  onItemClick(event: { route: string }, attendee: CalendarAttendee): void {
    this.addEntry.emit({ event, attendee });
  }

  onNavigate(): void {
    const attendee = this.attendee();
    if (attendee.isInMaximizer && attendee.id) {
      this.navigate.emit(attendee);
    }
  }

  onAddEntryOpen(): void {
    this.actionButton()?.toggle(false);
  }

  onActionOpen(): void {
    this.addEntryButton()?.toggle(false);
  }

  private async moveAttendee(attendee: CalendarAttendee): Promise<void> {
    const success = await this.tryMoveAttendee(attendee);

    this.notify.emit(
      success
        ? {
            type: 'success',
            message: attendee.isRequired
              ? 'outlook.calendar.compose.moveAttendee.optionalSuccess'
              : 'outlook.calendar.compose.moveAttendee.requiredSuccess',
          }
        : {
            type: 'error',
            message: 'outlook.calendar.compose.moveAttendee.error',
          },
    );

    this.tracker.trackEvent({
      module: EventModule.Compose,
      journey: EventJourney.AddRecipient,
      action: EventAction.Completed,
      metadata: {
        attendeeType: attendee.isRequired
          ? EventMetadata.RequiredToOptional
          : EventMetadata.OptionalToRequired,
        result: success ? EventMetadata.Success : EventMetadata.Failed,
      },
    });
  }

  private async tryMoveAttendee(attendee: CalendarAttendee): Promise<boolean> {
    try {
      await this.outlookService.removeCalendarAttendee(
        attendee.emailAddress,
        attendee.isRequired,
      );
      await this.outlookService.addCalendarAttendee(
        attendee.emailAddress,
        attendee.displayName,
        !attendee.isRequired,
      );
    } catch {
      await this.restoreAttendee(attendee);
      return false;
    }

    this.calendarStore.removeAttendee(attendee.emailAddress);
    this.calendarStore.addAttendee({
      ...attendee,
      isRequired: !attendee.isRequired,
    });
    return true;
  }

  private async restoreAttendee(attendee: CalendarAttendee): Promise<void> {
    try {
      await this.outlookService.addCalendarAttendee(
        attendee.emailAddress,
        attendee.displayName,
        attendee.isRequired,
      );
    } catch {
      // Best-effort restore; caller already surfaces the original error.
    }
  }

  private async copyEntryLink(attendee: CalendarAttendee): Promise<void> {
    if (!attendee.id || !attendee.type) {
      return;
    }

    const copied = await this.entryLinkService.copyEntryLink(
      attendee.id,
      attendee.type,
      attendee.abentryType ?? attendee.type,
    );

    this.notify.emit(
      copied
        ? {
            type: 'success',
            message: this.translate.instant(
              'outlook.entryPage.actions.entryCopied',
              {
                type: this.entrySearchService.entryTypeLabel(
                  attendee.type,
                  attendee.abentryType,
                ),
              },
            ),
          }
        : { type: 'error', message: 'outlook.errors.generic' },
    );
  }

  private async removeAttendee(attendee: CalendarAttendee): Promise<void> {
    try {
      await this.outlookService.removeCalendarAttendee(
        attendee.emailAddress,
        attendee.isRequired,
      );
      this.calendarStore.removeAttendee(attendee.emailAddress);
    } catch {
      this.notify.emit({
        type: 'error',
        message: 'outlook.calendar.compose.removeAttendee.error',
      });
    }
  }
}
